import InfoIcon from '@mui/icons-material/Info';
import { getBusinessInvoices } from 'lib/api/business';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { showBusinessNetwork } from 'services/redux/businessNetworkSlice';

const BusinessInvoicesReceived = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [receivedInvoices, setReceivedInvoices] = useState([]);
  const { id } = useParams<{ id: string }>();
  const [endDate, setEndDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [folio, setFolio] = useState('');
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (businessIdentifier)
      query += `stakeholderIdentifier=${businessIdentifier}&`;
    if (businessName) query += `businessName=${businessName}&`;
    if (folio) query += `folio=${folio}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}`;
    return query;
  };

  const searchReceivedInvoices = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    if (!id) return;

    setLoading(true);
    try {
      const query = generateQuery({ page, pageSize });
      const response = await getBusinessInvoices(
        dispatch,
        id,
        'received',
        query,
      );
      setReceivedInvoices(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const triggerSearch = async () => {
    setLoading(true);
    await searchReceivedInvoices({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  const filters: Filter[] = [
    {
      label: 'Fecha de emisión',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de emisión',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
    {
      label: 'Rut emisor',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Nombre emisor',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
  ];

  useEffect(() => {
    void searchReceivedInvoices({ page: 0, pageSize: PAGE_SIZE });
  }, []);

  const handleShowBusinessDetails = (id: string) => {
    const row: any = receivedInvoices.find((x: any) => x.id === id);
    if (!row) return;
    dispatch(
      showBusinessNetwork({
        id: row.businessIdentifier,
        name: row.businessName,
      }),
    );
  };

  return (
    <div>
      <HeaderWithActions
        buttons={[
          {
            name: 'Filtro',
            action: () => {},
            color: 'primary',
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={triggerSearch}
                dispatch={dispatch}
                disableSearch={loading}
              />
            ),
          },
        ]}
        title="Aquí verás las facturas recibidas."
        variant="body1"
      />
      <TableWithPagination
        tableSize="small"
        exportToFile="xlsx"
        dataHeaders={[
          { key: 'folio', label: 'Folio' },
          { key: 'code', label: 'Cód.' },
          { key: 'businessName', label: 'Emisor' },
          {
            key: 'businessIdentifier',
            label: 'Rut Emisor',
            handleAction: handleShowBusinessDetails,
            icon: <InfoIcon fontSize="small" />,
          },
          { key: 'amount', label: 'Monto', type: 'money' },
          { key: 'cancelDate', label: 'Reclamo', type: 'date' },
          { key: 'creditNoteAmount', label: 'NC', type: 'money-colored' },
          { key: 'confirmDate', label: 'Acuse', type: 'date' },
          {
            key: 'issuedDate',
            label: 'Emisión',
            type: 'eight-days-date',
          },
          { key: 'cessionary', label: 'Cedida' },
          { key: 'status', label: 'Estado', type: 'cession' },
        ]}
        data={receivedInvoices}
        loading={loading}
        setData={setReceivedInvoices}
        searchData={searchReceivedInvoices}
        setPagination={setPagination}
        pagination={pagination}
        lastElement
      />
    </div>
  );
};

export default BusinessInvoicesReceived;
